import { TourStep } from "./tour-steps";

/**
 * Per-page tour steps — shown the first time a center manager or tutor
 * lands on a specific page. Uses data-tour-page attributes on page elements.
 */

const pageTours: Record<string, { roles: string[]; steps: TourStep[] }> = {
  "learning-dashboard": {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='stats-cards']",
        title: "Key Metrics",
        description: "Your top-level learning statistics — courses completed, in progress, and overdue.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='course-progress']",
        title: "Course Progress",
        description: "Visual breakdown of your team's course completion rates.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='recent-activity']",
        title: "Recent Activity",
        description: "The latest learning activity from your team in real time.",
        placement: "left",
      },
    ],
  },
  "learning-my-progress": {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='enrolled-courses']",
        title: "Your Enrolled Courses",
        description: "All courses you're enrolled in with progress tracking.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='achievements']",
        title: "Achievements & Badges",
        description: "Celebrate your learning milestones and earned badges.",
        placement: "bottom",
      },
    ],
  },
  "learning-progress-center": {
    roles: ["center_manager"],
    steps: [
      {
        target: "[data-tour-page='location-filter']",
        title: "Filter by Centre",
        description: "Select a centre to view staff learning progress.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='user-table']",
        title: "Staff Progress Table",
        description: "Detailed view of each staff member's course progress, completions, and overdue items.",
        placement: "top",
      },
    ],
  },
  "learning-courses": {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='course-filters']",
        title: "Filter & Search",
        description: "Filter courses by category, status, or search by name.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='course-grid']",
        title: "Course Cards",
        description: "Browse available courses. Click any card to view details and start learning.",
        placement: "top",
      },
    ],
  },
  "learning-tutor-audit": {
    roles: ["center_manager"],
    steps: [
      {
        target: "[data-tour-page='audit-filters']",
        title: "Audit Filters",
        description: "Filter by centre, tutor, or date range to narrow your audit view.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='audit-table']",
        title: "Audit Results",
        description: "Review tutor compliance, course completions, and performance metrics.",
        placement: "top",
      },
    ],
  },
  "assessment-dashboard": {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='quiz-stats']",
        title: "Quiz Statistics",
        description: "Overview of quiz assignments, completions, and pass rates.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='recent-quizzes']",
        title: "Recent Quizzes",
        description: "Latest quiz activity from your students.",
        placement: "top",
      },
    ],
  },
  "assessment-diagnostic-tests": {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='quiz-list']",
        title: "Quiz Library",
        description: "Browse, create, and manage diagnostic quizzes for your students.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='assign-button']",
        title: "Assign Quizzes",
        description: "Select quizzes and assign them to individual students or groups.",
        placement: "bottom",
      },
    ],
  },
  "assessment-students": {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='student-search']",
        title: "Find Students",
        description: "Search and filter students by name, grade, or status.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='student-table']",
        title: "Student List",
        description: "View all students with their quiz scores, assignments, and engagement data.",
        placement: "top",
      },
    ],
  },
  "assessment-assignments": {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='assignment-list']",
        title: "Assignment List",
        description: "View all assignments with due dates, submission status, and grading.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='create-assignment']",
        title: "Create Assignment",
        description: "Create new homework or coursework assignments for your students.",
        placement: "bottom",
      },
    ],
  },
  "assessment-early-warning": {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='risk-overview']",
        title: "Risk Overview",
        description: "At-a-glance view of students flagged as at-risk based on engagement patterns.",
        placement: "bottom",
      },
      {
        target: "[data-tour-page='alert-list']",
        title: "Active Alerts",
        description: "Review and action alerts for students who need extra support.",
        placement: "top",
      },
    ],
  },
  "assessment-scoring": {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='scoring-queue']",
        title: "Scoring Queue",
        description: "Open-ended questions waiting for manual review and scoring.",
        placement: "bottom",
      },
    ],
  },
  "assessment-exams": {
    roles: ["center_manager"],
    steps: [
      {
        target: "[data-tour-page='exam-list']",
        title: "Exam Library",
        description: "Manage formal exams with sections and time limits.",
        placement: "bottom",
      },
    ],
  },
  "assessment-messages": {
    roles: ["center_manager"],
    steps: [
      {
        target: "[data-tour-page='chat-list']",
        title: "Parent Conversations",
        description: "View and respond to messages from parents about their children.",
        placement: "right",
      },
    ],
  },
  forum: {
    roles: ["center_manager", "tutor"],
    steps: [
      {
        target: "[data-tour-page='forum-categories']",
        title: "Discussion Categories",
        description: "Browse discussion topics organised by category.",
        placement: "bottom",
      },
    ],
  },
};

/**
 * Get page tour steps for a given page and role.
 * Returns null if no tour is defined or role doesn't match.
 */
export function getPageTourSteps(
  pageKey: string,
  role: string | null
): TourStep[] | null {
  const config = pageTours[pageKey];
  if (!config || !role || !config.roles.includes(role)) return null;
  return config.steps;
}

/**
 * Derive a page key from the current pathname.
 * e.g. "/learning/dashboard" → "learning-dashboard"
 *      "/assessment/students" → "assessment-students"
 */
export function getPageKeyFromPath(pathname: string): string | null {
  // Strip leading slash and convert slashes to dashes
  const clean = pathname.replace(/^\//, "").replace(/\//g, "-");
  if (!clean) return null;
  return clean;
}
